import { industry_offer_content } from '../industry_offer_content';

const visible_by_service_keys = (selectedServiceKeys, sectionData) => {
    if (!sectionData?.triggers?.length) {
        return true;
    }

    return sectionData.triggers.some((serviceKey) => selectedServiceKeys.includes(serviceKey));
};

const merge_industry_content = (industryKey, contentKey = '', contentMap = industry_offer_content) => {
    const defaultContent = contentMap.general || {};
    const industryContent = contentMap[industryKey] || {};
    const requestedContent = contentMap[contentKey] || {};

    return {
        ...defaultContent,
        ...industryContent,
        ...requestedContent
    };
};

const read_unique_values = (values) => {
    return [...new Set((values || []).filter(Boolean))];
};

const channel_by_service_lookup = {
    social_media_management: ['Facebook', 'Instagram'],
    meta_ads: ['Meta Ads'],
    google_ads: ['Google Ads'],
    tiktok_ads: ['TikTok Ads'],
    email_marketing: ['Email Marketing'],
    blog: ['Blog'],
    email_journey: ['Email Journey'],
    profile_creation: ['Facebook', 'Instagram'],
    social_media_creation: ['Facebook', 'Instagram']
};

const assumptions_by_service_lookup = {
    el: {
        social_media_management: ['Διαχείριση προφίλ επιχείρησης για ενεργή παρουσία στα social.'],
        meta_ads: ['Δημιουργία και βελτιστοποίηση Meta Campaigns.'],
        google_ads: [
            'Δημιουργία και διαχείριση διαφημιστικών εκστρατειών Google.',
            'Ρύθμιση conversion tracking και παρακολούθηση αποτελεσμάτων.'
        ],
        tiktok_ads: ['Δημιουργία και βελτιστοποίηση TikTok διαφημιστικών εκστρατειών.'],
        email_marketing: ['Δημιουργία και βελτιστοποίηση καμπανιών email marketing.'],
        blog: ['Δημιουργία θεματολογίας και δημοσίευση άρθρων blog.'],
        email_journey: ['Σχεδιασμός email journeys και αυτοματισμών επικοινωνίας.'],
        profile_creation: ['Δημιουργία και ρύθμιση επαγγελματικών προφίλ σε Facebook και Instagram.'],
        video_edit: ['Παράδοση επεξεργασμένου βίντεο σε κατάλληλο format χρήσης.'],
        static_ad: ['Παράδοση στατικού εικαστικού με σωστή branded δομή.'],
        logos: ['Παράδοση λογοτύπου σε format κατάλληλο για digital και print χρήση.'],
        card: ['Παράδοση τελικού αρχείου κάρτας για εκτύπωση.'],
        flyers_banners: ['Παράδοση τελικού δημιουργικού σε διαστάσεις προβολής.'],
        catalog: ['Παράδοση τελικού καταλόγου για ψηφιακή ή έντυπη χρήση.'],
        social_media_creation: ['Βασική παραμετροποίηση εταιρικής παρουσίας σε Facebook/Instagram.'],
        graphics: ['Παράδοση custom γραφιστικού υλικού βάσει επιβεβαιωμένου brief.'],
        plan_starter: ['Υλοποίηση Starter website με βάση το συμφωνημένο scope.'],
        plan_business: ['Υλοποίηση Business website με βάση το συμφωνημένο scope.'],
        eshop: ['Υλοποίηση eShop με βάση το συμφωνημένο scope και τις επιχειρησιακές απαιτήσεις.'],
        plan_eshop: ['Υλοποίηση eShop με βάση το συμφωνημένο scope και τις επιχειρησιακές απαιτήσεις.'],
        maintenance_starter: ['Μηνιαία τεχνική συντήρηση website με βασικές εργασίες υποστήριξης.'],
        maintenance_business: ['Μηνιαία τεχνική συντήρηση website με διευρυμένες εργασίες υποστήριξης.']
    },
    en: {
        social_media_management: ['Business profile management for active social presence.'],
        meta_ads: ['Meta campaign creation and optimization.'],
        google_ads: [
            'Google campaign creation and management.',
            'Conversion tracking setup and ongoing monitoring.'
        ],
        tiktok_ads: ['TikTok campaign creation and optimization.'],
        email_marketing: ['Email marketing campaign creation and optimization.'],
        blog: ['Blog topic planning and publication flow.'],
        email_journey: ['Email journey and automation setup.'],
        profile_creation: ['Business profile creation and setup on Facebook and Instagram.'],
        video_edit: ['Edited video delivery in production-ready formats.'],
        static_ad: ['Static creative delivery with branded structure.'],
        logos: ['Logo delivery in digital and print-ready formats.'],
        card: ['Final business card print-ready file delivery.'],
        flyers_banners: ['Final flyer/banner delivery in required dimensions.'],
        catalog: ['Final catalog delivery for digital or print use.'],
        social_media_creation: ['Baseline business presence setup on Facebook and Instagram.'],
        graphics: ['Custom graphics delivery based on approved brief.'],
        plan_starter: ['Starter website delivery based on approved scope.'],
        plan_business: ['Business website delivery based on approved scope.'],
        eshop: ['eShop delivery based on approved scope and business requirements.'],
        plan_eshop: ['eShop delivery based on approved scope and business requirements.'],
        maintenance_starter: ['Monthly technical maintenance with baseline support tasks.'],
        maintenance_business: ['Monthly technical maintenance with extended support tasks.']
    }
};

const read_dynamic_channels = (selectedServiceKeys, fallbackChannels) => {
    const channels = selectedServiceKeys.flatMap((serviceKey) => {
        return channel_by_service_lookup[serviceKey] || [];
    });

    if (!selectedServiceKeys.length) {
        return read_unique_values(fallbackChannels || []);
    }

    if (!channels.length) {
        return [];
    }

    return read_unique_values(channels);
};

const read_dynamic_assumptions = (selectedServiceKeys, fallbackAssumptions, offerLanguage = 'el') => {
    const assumptionsLookup = assumptions_by_service_lookup[offerLanguage] || assumptions_by_service_lookup.el;
    const assumptions = selectedServiceKeys.flatMap((serviceKey) => {
        return assumptionsLookup[serviceKey] || [];
    });
    const marketingServiceKeys = [
        'social_media_management',
        'meta_ads',
        'google_ads',
        'tiktok_ads',
        'email_marketing',
        'blog',
        'email_journey'
    ];
    const hasMarketingService = selectedServiceKeys.some((serviceKey) => marketingServiceKeys.includes(serviceKey));

    if (!selectedServiceKeys.length) {
        return read_unique_values(fallbackAssumptions || []);
    }

    if (!assumptions.length) {
        return [];
    }

    return read_unique_values([
        ...assumptions,
        ...(hasMarketingService
            ? [offerLanguage === 'en'
                ? 'Monthly performance report with analysis.'
                : 'Μηνιαία αναφορά απόδοσης με ανάλυση αποτελεσμάτων.']
            : [])
    ]);
};

const read_dynamic_out_of_scope = (baseOutOfScopeItems, unselectedServiceNames) => {
    const excludedUnselectedNames = [
        'starter',
        'business',
        'eshop',
        'web maintenance starter',
        'web maintenance business',
        'custom'
    ];
    const unselectedItems = (unselectedServiceNames || [])
        .filter((serviceName) => {
            const normalizedServiceName = `${serviceName || ''}`.trim().toLowerCase();

            return !excludedUnselectedNames.includes(normalizedServiceName);
        })
        .map((serviceName) => {
            return serviceName;
        });

    return read_unique_values([
        ...(baseOutOfScopeItems || []),
        ...unselectedItems
    ]);
};

export const build_offer_report = (params) => {
    const selectedServiceKeys = Array.isArray(params?.selectedServiceKeys) ? params.selectedServiceKeys : [];
    const unselectedServiceNames = Array.isArray(params?.unselectedServiceNames) ? params.unselectedServiceNames : [];
    const industryKey = params?.industryKey || '';
    const contentKey = params?.contentKey || '';
    const offerLanguage = params?.offerLanguage === 'en' ? 'en' : 'el';
    const reportContent = merge_industry_content(industryKey, contentKey, params?.contentMap || industry_offer_content);
    const reportServiceSections = Array.isArray(reportContent?.serviceSections) ? reportContent.serviceSections : [];

    return {
        brandOverviewFallback: reportContent.brandOverviewFallback || '',
        digitalChannels: visible_by_service_keys(selectedServiceKeys, reportContent.digitalChannels)
            ? read_dynamic_channels(selectedServiceKeys, reportContent.digitalChannels?.items || [])
            : [],
        socialManagementItems: visible_by_service_keys(selectedServiceKeys, reportContent.socialManagement) ? (reportContent.socialManagement?.items || []) : [],
        contentFlowItems: visible_by_service_keys(selectedServiceKeys, reportContent.contentFlow) ? (reportContent.contentFlow?.steps || []) : [],
        serviceSections: reportServiceSections.filter((serviceSection) => {
            return Array.isArray(serviceSection?.serviceKeys)
                ? serviceSection.serviceKeys.some((serviceKey) => selectedServiceKeys.includes(serviceKey))
                : false;
        }),
        prerequisitesItems: reportContent.prerequisitesItems || [],
        assumptionsItems: read_dynamic_assumptions(selectedServiceKeys, reportContent.assumptionsItems || [], offerLanguage),
        outOfScopeItems: read_dynamic_out_of_scope(reportContent.outOfScopeItems || [], unselectedServiceNames),
        finalTerms: reportContent.finalTerms || [],
        importantItems: reportContent.importantItems || []
    };
};
